import { criarPedidoSchema, type CriarPedidoDto, type PedidoVendaResponse, type StatusPedido } from '@triade/contracts';
import type { ItemResumo } from '@/components/item-combobox';
import { decimalDigitado, ehDecimal, formatarDecimal } from '@/lib/format';

/*
 * Formulario do pedido: so texto <-> string decimal da API. Nenhum valor e calculado aqui (a MC vem da resposta).
 * Unica conta: deslocar o ponto decimal na string (percentual digitado 18 = fracao "0.18"), sem Number.
 */

export const STATUS_PEDIDO_ROTULO: Record<StatusPedido, string> = {
  CONCLUIDO: 'Concluído',
  CONFIRMADO: 'Confirmado',
  PREVISAO: 'Previsão',
  PENDENTE: 'Pendente',
};
export const STATUS_PEDIDO_ORDEM: readonly StatusPedido[] = ['CONCLUIDO', 'CONFIRMADO', 'PREVISAO', 'PENDENTE'];

export function rotuloStatus(status: StatusPedido): string {
  return STATUS_PEDIDO_ROTULO[status];
}

/**
 * Move o ponto decimal de uma string decimal `n` casas (n > 0 multiplica por 10^n; n < 0 divide). So reescreve digitos.
 * "18" com -2 vira "0.18"; "0.18000000" com 2 vira "18".
 */
export function deslocarPonto(dec: string, n: number): string {
  const neg = dec.startsWith('-');
  const sem = neg ? dec.slice(1) : dec;
  const [inteiro = '0', fracao = ''] = sem.split('.');
  let digitos = inteiro + fracao;
  let pos = inteiro.length + n;
  if (pos < 0) {
    digitos = '0'.repeat(-pos) + digitos;
    pos = 0;
  }
  if (pos > digitos.length) digitos = digitos.padEnd(pos, '0');
  const parteInteira = digitos.slice(0, pos).replace(/^0+(?=\d)/, '') || '0';
  const parteFracao = digitos.slice(pos).replace(/0+$/, '');
  return `${neg ? '-' : ''}${parteInteira}${parteFracao ? `.${parteFracao}` : ''}`;
}

/** Percentual digitado ("18,5") para fracao da API ("0.185"). Invalido: null. */
export function percentualParaFracao(texto: string): string | null {
  const dec = decimalDigitado(texto);
  return dec === null ? null : deslocarPonto(dec, -2);
}

/** Fracao da API ("0.18000000") para o texto do campo ("18") em pt-BR, sem arredondar. */
export function fracaoParaPercentualTexto(fracao: string | null | undefined): string {
  return ehDecimal(fracao) ? formatarDecimal(deslocarPonto(fracao.trim(), 2), 0) : '';
}

export type TipoFrete = 'total' | 'porUnidade';

export interface PedidoForm {
  clienteId: string;
  item: ItemResumo | null;
  quantidade: string;
  precoUnitario: string;
  dataEmbarque: string;
  previsaoEntrega: string;
  status: StatusPedido;
  modalidadeFrete: '' | 'CIF' | 'FOB';
  transportadora: string;
  tipoFrete: TipoFrete;
  frete: string;
  comissao: string;
  vendedorNome: string;
  numeroPedidoCliente: string;
  observacao: string;
  /** Pedido sem custo: fica fora da MC e do resultado do mes (a API decide o resto). */
  semCusto: boolean;
}

/** Pedido novo: embarque no primeiro dia do mes da tela. */
export function pedidoFormVazio(mes: string): PedidoForm {
  return {
    clienteId: '',
    item: null,
    quantidade: '',
    precoUnitario: '',
    dataEmbarque: `${mes}-01`,
    previsaoEntrega: '',
    status: 'PREVISAO',
    modalidadeFrete: '',
    transportadora: '',
    tipoFrete: 'total',
    frete: '',
    comissao: '',
    vendedorNome: '',
    numeroPedidoCliente: '',
    observacao: '',
    semCusto: false,
  };
}

/** Pedido existente para edicao: valores da API no texto do campo (pt-BR, sem arredondar). */
export function pedidoFormDe(p: PedidoVendaResponse): PedidoForm {
  const tipoPorTotal = p.freteTotal !== undefined;
  return {
    clienteId: p.clienteId,
    item: { id: p.itemId, codigo: p.itemCodigo, descricao: p.itemDescricao },
    quantidade: formatarDecimal(p.quantidade, 0),
    precoUnitario: p.precoUnitario !== undefined ? formatarDecimal(p.precoUnitario, 0) : '',
    dataEmbarque: p.dataEmbarque,
    previsaoEntrega: p.previsaoEntrega ?? '',
    status: p.status,
    modalidadeFrete: p.modalidadeFrete ?? '',
    transportadora: p.transportadora ?? '',
    tipoFrete: 'total',
    frete: tipoPorTotal ? formatarDecimal(p.freteTotal, 0) : '',
    comissao: fracaoParaPercentualTexto(p.comissaoPct),
    vendedorNome: p.vendedorNome ?? '',
    numeroPedidoCliente: p.numeroPedidoCliente ?? '',
    observacao: p.observacao ?? '',
    semCusto: p.semCusto === true,
  };
}

export type CampoPedido = 'clienteId' | 'itemId' | 'quantidade' | 'precoUnitario' | 'dataEmbarque' | 'previsaoEntrega' | 'frete' | 'comissao' | 'geral';

export type MontagemPedido = { ok: true; corpo: CriarPedidoDto } | { ok: false; campo: CampoPedido; mensagem: string };

const CAMPO_DO_SCHEMA: Record<string, CampoPedido> = {
  clienteId: 'clienteId',
  itemId: 'itemId',
  quantidade: 'quantidade',
  precoUnitario: 'precoUnitario',
  dataEmbarque: 'dataEmbarque',
  previsaoEntrega: 'previsaoEntrega',
  freteTotal: 'frete',
  fretePorUnidade: 'frete',
  comissaoPct: 'comissao',
};

const opcional = (s: string): string | null => (s.trim() ? s.trim() : null);

/** Texto do formulario para o corpo da API (validado pelo schema compartilhado). */
export function montarCorpoPedido(f: PedidoForm): MontagemPedido {
  const erro = (campo: CampoPedido, mensagem: string): MontagemPedido => ({ ok: false, campo, mensagem });
  if (!f.clienteId) return erro('clienteId', 'Escolha o cliente');
  if (!f.item) return erro('itemId', 'Escolha o produto');
  const quantidade = decimalDigitado(f.quantidade);
  if (quantidade === null) return erro('quantidade', 'Informe a quantidade em toneladas');
  const precoUnitario = decimalDigitado(f.precoUnitario);
  if (precoUnitario === null) return erro('precoUnitario', 'Informe o preço por unidade');
  let frete: string | null = null;
  if (f.frete.trim()) {
    frete = decimalDigitado(f.frete);
    if (frete === null) return erro('frete', 'Valor de frete inválido');
  }
  let comissaoPct: string | null = null;
  if (f.comissao.trim()) {
    comissaoPct = percentualParaFracao(f.comissao);
    if (comissaoPct === null) return erro('comissao', 'Comissão inválida (ex.: 3 para 3%)');
  }
  const corpo = {
    clienteId: f.clienteId,
    itemId: f.item.id,
    quantidade,
    precoUnitario,
    dataEmbarque: f.dataEmbarque,
    previsaoEntrega: opcional(f.previsaoEntrega),
    status: f.status,
    modalidadeFrete: f.modalidadeFrete || null,
    transportadora: opcional(f.transportadora),
    freteTotal: frete !== null && f.tipoFrete === 'total' ? frete : null,
    fretePorUnidade: frete !== null && f.tipoFrete === 'porUnidade' ? frete : null,
    comissaoPct,
    vendedorNome: opcional(f.vendedorNome),
    numeroPedidoCliente: opcional(f.numeroPedidoCliente),
    observacao: opcional(f.observacao),
    semCusto: f.semCusto,
  };
  const valido = criarPedidoSchema.safeParse(corpo);
  if (!valido.success) {
    const issue = valido.error.issues[0];
    const chave = String(issue?.path[0] ?? '');
    return erro(CAMPO_DO_SCHEMA[chave] ?? 'geral', issue?.message ?? 'Dados do pedido inválidos');
  }
  return { ok: true, corpo: valido.data };
}
